import React, { useState, useEffect } from 'react';
import { metadataAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PasswordStrengthChecker, { meetsPolicy } from '../components/PasswordStrengthChecker';
import toast from 'react-hot-toast';
import { Plus, Users, Eye, EyeOff, Pencil, UserX, UserCheck, X } from 'lucide-react';

export default function ClientUsersPage() {
  const { isAdmin, isSupportLead } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // New user form
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    metadataAPI.getClients().then(r => setClients(r.data)).catch(() => {});
  }, []);

  const loadUsers = async (clientId) => {
    if (!clientId) { setUsers([]); return; }
    setLoading(true);
    try {
      const resp = await metadataAPI.getClientUsers(clientId, true);
      setUsers(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (id) => {
    setSelectedClient(id);
    loadUsers(id);
    setShowForm(false);
    setEditingId(null);
    resetForm();
  };

  const resetForm = () => {
    setNewEmail('');
    setNewName('');
    setNewPassword('');
    setShowPassword(false);
  };

  const canSubmit = newEmail && newName && meetsPolicy(newPassword) && !creating;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    try {
      await authAPI.createClientUser({
        email: newEmail,
        name: newName,
        password: newPassword,
        client_id: parseInt(selectedClient),
      });
      toast.success(`User ${newName} created`);
      resetForm();
      setShowForm(false);
      loadUsers(selectedClient);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join('; '));
      } else {
        toast.error('Failed to create user');
      }
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setEditName(user.name);
    setEditPassword('');
    setShowEditPassword(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditPassword('');
    setShowEditPassword(false);
  };

  const handleSaveEdit = async (userId) => {
    const payload = {};
    if (editName) payload.name = editName;
    if (editPassword) payload.password = editPassword;
    if (!payload.name && !payload.password) { cancelEdit(); return; }
    if (editPassword && !meetsPolicy(editPassword)) {
      toast.error('Password does not meet strength requirements');
      return;
    }
    setSaving(true);
    try {
      await authAPI.updateClientUser(userId, payload);
      toast.success('User updated');
      cancelEdit();
      loadUsers(selectedClient);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate ${user.name} (${user.email})? They will no longer be able to log in.`)) return;
    try {
      await authAPI.deactivateClientUser(user.id);
      toast.success(`${user.name} deactivated`);
      loadUsers(selectedClient);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deactivation failed');
    }
  };

  const handleReactivate = async (user) => {
    try {
      await authAPI.updateClientUser(user.id, { is_active: true });
      toast.success(`${user.name} reactivated`);
      loadUsers(selectedClient);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reactivation failed');
    }
  };

  if (!isAdmin && !isSupportLead) {
    return (
      <div>
        <div className="page-header"><h1>Client users</h1></div>
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          Access restricted to Support Lead and Admin.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Client users</h1>
        <p>Create and manage client user accounts for the support portal</p>
      </div>

      <div className="card">
        {/* Client selector */}
        <div style={{ padding: '0 16px 16px', display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: 400 }}>
            <label className="form-label">Client</label>
            <select className="form-select" value={selectedClient} onChange={e => handleClientChange(e.target.value)}>
              <option value="">Select client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>
              ))}
            </select>
          </div>
          {selectedClient && (
            <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); cancelEdit(); }}>
              <Plus size={14} /> {showForm ? 'Cancel' : 'New user'}
            </button>
          )}
        </div>

        {/* Create user form */}
        {showForm && selectedClient && (
          <div style={{ margin: '0 16px 16px', padding: 16, background: 'var(--color-bg-subtle, #f9fafb)', borderRadius: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Create client user</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="form-label">Full name <span className="required">*</span></label>
                <input className="form-input" value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Maria Santos" />
              </div>
              <div>
                <label className="form-label">Email <span className="required">*</span></label>
                <input type="email" className="form-input" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  placeholder="e.g. maria.santos@company.com" />
              </div>
            </div>
            <div style={{ maxWidth: 400, marginBottom: 12 }}>
              <label className="form-label">Password <span className="required">*</span></label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} className="form-input"
                  value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="Strong password required" style={{ paddingRight: 40 }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}
                  tabIndex={-1}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <PasswordStrengthChecker password={newPassword} />
            </div>
            <button className="btn btn-primary" onClick={handleCreate} disabled={!canSubmit}>
              {creating ? 'Creating...' : 'Create user'}
            </button>
          </div>
        )}

        {/* User list */}
        {selectedClient && !loading && (
          <div style={{ padding: '0 16px 16px' }}>
            {users.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                <Users size={20} style={{ marginBottom: 4, display: 'block', margin: '0 auto 8px' }} />
                No users for this client. Click "New user" to create one.
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => editingId === u.id ? (
                    /* ── Inline edit row ── */
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-subtle, #f9fafb)' }}>
                      <td style={{ padding: 8 }}>
                        <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)}
                          style={{ fontSize: 13, padding: '4px 8px' }} />
                      </td>
                      <td style={{ padding: 8, color: 'var(--color-text-muted)' }}>{u.email}</td>
                      <td style={{ padding: 8 }}>
                        <div style={{ maxWidth: 260 }}>
                          <label style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Reset password (optional)</label>
                          <div style={{ position: 'relative', marginTop: 2 }}>
                            <input type={showEditPassword ? 'text' : 'password'} className="form-input"
                              value={editPassword} onChange={e => setEditPassword(e.target.value)}
                              placeholder="Leave blank to keep current"
                              style={{ fontSize: 12, padding: '4px 8px', paddingRight: 32 }} />
                            <button type="button" onClick={() => setShowEditPassword(!showEditPassword)}
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}
                              tabIndex={-1}>
                              {showEditPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                          </div>
                          {editPassword && <PasswordStrengthChecker password={editPassword} />}
                        </div>
                      </td>
                      <td style={{ padding: 8, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-primary btn-sm" disabled={saving || !editName}
                            onClick={() => handleSaveEdit(u.id)} style={{ fontSize: 11 }}>
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button className="btn btn-sm" onClick={cancelEdit} style={{ fontSize: 11 }}>
                            <X size={12} /> Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    /* ── Normal display row ── */
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border)', opacity: u.is_active ? 1 : 0.55 }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>{u.name}</td>
                      <td style={{ padding: '8px', color: 'var(--color-text-muted)' }}>{u.email}</td>
                      <td style={{ padding: '8px' }}>
                        {u.is_active
                          ? <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Active</span>
                          : <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Inactive</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {u.is_active && (
                            <button className="btn btn-sm" onClick={() => startEdit(u)} title="Edit user"
                              style={{ fontSize: 11, padding: '3px 8px' }}>
                              <Pencil size={12} /> Edit
                            </button>
                          )}
                          {u.is_active ? (
                            <button className="btn btn-sm" onClick={() => handleDeactivate(u)} title="Deactivate user"
                              style={{ fontSize: 11, padding: '3px 8px', color: 'var(--color-danger, #dc2626)' }}>
                              <UserX size={12} /> Deactivate
                            </button>
                          ) : (
                            <button className="btn btn-sm" onClick={() => handleReactivate(u)} title="Reactivate user"
                              style={{ fontSize: 11, padding: '3px 8px', color: '#059669' }}>
                              <UserCheck size={12} /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {selectedClient && loading && (
          <p style={{ padding: '16px', color: 'var(--color-text-muted)', fontSize: 13 }}>Loading users...</p>
        )}
      </div>
    </div>
  );
}

const thStyle = { textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)' };
